import { useEffect, useRef, useState, type ReactElement } from "react";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { CalendarDays } from "lucide-react";
import { Button } from "components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Text } from "components/text";
import { useAnalytics } from "contexts/AnalyticsContext";
import type { SeasonalEventSurface } from "contexts/AnalyticsContext/AnalyticsEvents";
import type {
  SeasonalEvent,
  SeasonalEventPrompt,
} from "integrations/firebase/collections/seasonalEvents";
import { RegionBadge } from "./RegionBadge";
import { SeasonalEventCalendar } from "./SeasonalEventCalendar";
import { SemanticSearchNotice } from "./SemanticSearchNotice";
import { SuggestedSearches } from "./SuggestedSearches";
import type { CalendarEventEntry } from "./calendarUtils";
import { useSeasonalToday } from "./hooks/useSeasonalToday";
import { getNextOccurrenceStart } from "./utils";

type ModalView = "event" | "calendar";
type PendingFocus = "calendar-back" | "view-calendar" | "event-title";

export interface SeasonalEventModalProps {
  /** Null opens straight on the calendar, with no event selected yet. */
  initialEvent: SeasonalEvent | null;
  events: SeasonalEvent[];
  surface: SeasonalEventSurface;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRunSearch: (
    prompt: SeasonalEventPrompt,
    refreshIndex: number,
    eventId: string,
  ) => void;
}

const viewFor = (event: SeasonalEvent | null): ModalView =>
  event ? "event" : "calendar";

export const SeasonalEventModal = ({
  initialEvent,
  events,
  surface,
  open,
  onOpenChange,
  onRunSearch,
}: SeasonalEventModalProps): ReactElement => {
  const { t } = useTranslation(["components"]);
  const { gaEvent } = useAnalytics();
  const [view, setView] = useState<ModalView>(() => viewFor(initialEvent));
  const [activeEvent, setActiveEvent] = useState<SeasonalEvent | null>(
    initialEvent,
  );
  const [activeOccurrenceStart, setActiveOccurrenceStart] = useState<
    string | null
  >(null);
  const [prevInitialEvent, setPrevInitialEvent] = useState(initialEvent);
  const [prevOpen, setPrevOpen] = useState(open);
  const viewCalendarRef = useRef<HTMLButtonElement>(null);
  const calendarBackRef = useRef<HTMLButtonElement>(null);
  const eventTitleRef = useRef<HTMLHeadingElement>(null);
  const pendingFocusRef = useRef<PendingFocus | null>(null);

  if (initialEvent?.event_id !== prevInitialEvent?.event_id) {
    setPrevInitialEvent(initialEvent);
    setActiveEvent(initialEvent);
    setActiveOccurrenceStart(null);
    setView(viewFor(initialEvent));
  }

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setView(viewFor(initialEvent));
      setActiveEvent(initialEvent);
      setActiveOccurrenceStart(null);
    }
  }

  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null) {
      return;
    }
    pendingFocusRef.current = null;

    const focusTarget = (): void => {
      if (target === "calendar-back") {
        calendarBackRef.current?.focus();
        return;
      }
      if (target === "view-calendar") {
        viewCalendarRef.current?.focus();
        return;
      }
      eventTitleRef.current?.focus();
    };

    const frame = requestAnimationFrame(focusTarget);
    return () => cancelAnimationFrame(frame);
  }, [view, activeEvent?.event_id]);

  const today = useSeasonalToday();
  const headerDate = activeEvent
    ? (activeOccurrenceStart ?? getNextOccurrenceStart(activeEvent, today))
    : undefined;
  const calendarBackLabel = t("components:seasonalEventModal.calendar.back");

  const handleOpenCalendar = (): void => {
    pendingFocusRef.current = "calendar-back";
    setView("calendar");
  };

  const handleBackToEvent = (): void => {
    pendingFocusRef.current = "view-calendar";
    setView("event");
  };

  const handleSelectCalendarEvent = (entry: CalendarEventEntry): void => {
    gaEvent({
      type: "calendar_event_selected",
      payload: {
        event_id: entry.event.event_id,
        lead_time_weeks: entry.leadTimeWeeks,
        surface,
      },
    });
    pendingFocusRef.current = "event-title";
    setActiveEvent(entry.event);
    setActiveOccurrenceStart(entry.occurrence.start);
    setView("event");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="seasonal-event-modal">
        {view === "event" && activeEvent ? (
          <DialogHeader className="gap-0 sm:text-left">
            <div className="flex items-start gap-3 pr-6">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary">
                <CalendarDays className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <DialogTitle ref={eventTitleRef} tabIndex={-1}>
                  {activeEvent.display_name}
                </DialogTitle>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <RegionBadge region={activeEvent.region} />
                  {headerDate ? (
                    <Text as="span" variant="muted">
                      {format(parseISO(headerDate), "d MMMM yyyy")}
                    </Text>
                  ) : null}
                </div>
              </div>
              <Button
                ref={viewCalendarRef}
                type="button"
                variant="link"
                size="small"
                className="shrink-0 gap-0.5 px-0 text-primary"
                label={t("components:seasonalEventModal.viewCalendar")}
                icon="chevron-right"
                iconPosition="right"
                onClick={handleOpenCalendar}
              />
            </div>
            <DialogDescription className="sr-only">
              {t("components:seasonalEventModal.description")}
            </DialogDescription>
          </DialogHeader>
        ) : (
          <DialogHeader className="gap-0 sm:text-left">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 pr-6">
              {activeEvent ? (
                <Button
                  ref={calendarBackRef}
                  type="button"
                  variant="link"
                  size="small"
                  className="shrink-0 justify-self-start gap-0.5 px-0"
                  label={calendarBackLabel}
                  icon="chevron-left"
                  iconPosition="left"
                  onClick={handleBackToEvent}
                />
              ) : (
                <span />
              )}
              <DialogTitle className="text-center">
                {t("components:seasonalEventModal.calendar.title")}
              </DialogTitle>
              <span />
            </div>
            <DialogDescription className="sr-only">
              {t("components:seasonalEventModal.calendar.description")}
            </DialogDescription>
          </DialogHeader>
        )}

        {/* Kept mounted while the calendar shows so its impression analytics
            fire once per open, not on every calendar round-trip. */}
        {activeEvent ? (
          <div className={view === "event" ? "flex flex-col gap-4" : "hidden"}>
            <SemanticSearchNotice surface={surface} />

            <SuggestedSearches
              key={activeEvent.event_id}
              eventId={activeEvent.event_id}
              prompts={activeEvent.prompts}
              surface={surface}
              isVisible={view === "event"}
              onRunSearch={onRunSearch}
            />
          </div>
        ) : null}

        {view === "calendar" ? (
          <SeasonalEventCalendar
            events={events}
            surface={surface}
            onSelectEvent={handleSelectCalendarEvent}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
